import React, { useEffect, useRef, useState } from 'react';
import { Menu, Label } from 'semantic-ui-react';
import { useComponentManager } from '../../providers/ComponentManagerProvider';
import { componentLabel } from './componentLabel';
import { PointsTab } from './PointsTab';
import { FunctionsTab } from './FunctionsTab';

const TABS = {
    POINTS: 'points',
    FUNCTIONS: 'functions',
};

export const ComponentDetailPanel = () => {
    const { selectedComponent, getComponentPoints, getComponentFunctions } = useComponentManager();

    const [activeTab, setActiveTab] = useState(TABS.POINTS);
    const [pointCount, setPointCount] = useState(0);
    const [functionCount, setFunctionCount] = useState(0);

    // Guards against out-of-order count loads when the component switches mid-flight.
    const countToken = useRef(0);

    const componentId = selectedComponent?.id;
    
    useEffect(() => {
        setActiveTab(TABS.POINTS);
        setPointCount(0);
        setFunctionCount(0);

        if (!componentId) return;

        const token = (countToken.current += 1);
        getComponentPoints(componentId)
            .then(res => {
                if (token !== countToken.current) return;
                setPointCount((res?.data ?? []).length);
            })
            .catch(() => {});
        getComponentFunctions(componentId)
            .then(res => {
                if (token !== countToken.current) return;
                setFunctionCount((res?.data ?? []).length);
            })
            .catch(() => {});
    }, [componentId, getComponentPoints, getComponentFunctions]);

    if (!selectedComponent) {
        return (
            <div className="component-manager-detail" data-testid="component-detail-empty">
                <p className="component-manager-detail-placeholder">
                    Select a component to view its details.
                </p>
            </div>
        );
    }

    return (
        <div className="component-manager-detail" data-testid="component-detail">
            <h2 className="component-manager-detail-title">
                {componentLabel(selectedComponent)}
            </h2>

            <Menu pointing secondary>
                <Menu.Item
                    name="Points"
                    active={activeTab === TABS.POINTS}
                    onClick={() => setActiveTab(TABS.POINTS)}
                    data-testid="tab-points"
                >
                    Points
                    <Label circular color="blue" size="small" data-testid="points-badge">
                        {pointCount}
                    </Label>
                </Menu.Item>
                <Menu.Item
                    name="Functions"
                    active={activeTab === TABS.FUNCTIONS}
                    onClick={() => setActiveTab(TABS.FUNCTIONS)}
                    data-testid="tab-functions"
                >
                    Functions
                    <Label circular color="blue" size="small" data-testid="functions-badge">
                        {functionCount}
                    </Label>
                </Menu.Item>
            </Menu>

            <div style={{ marginTop: '1rem' }}>
                {activeTab === TABS.POINTS && (
                    <PointsTab
                        key={selectedComponent.id}
                        componentId={selectedComponent.id}
                        onCountChange={setPointCount}
                    />
                )}
                {activeTab === TABS.FUNCTIONS && (
                    <FunctionsTab
                        key={selectedComponent.id}
                        componentId={selectedComponent.id}
                        onCountChange={setFunctionCount}
                    />
                )}
            </div>
        </div>
    );
};
